/**
 * useOptimisticCartMutations
 *
 * Wraps SCAPI basket mutations with:
 * - Optimistic local-state quantity updates (instant UI feedback, debounced API call)
 * - Cascade: quantity/remove operations propagate to all `_childItems` of complex items
 * - Per-item removing/updating flags so the UI can overlay a spinner and hide intermediate basket
 *   re-renders caused by sequential child-removal SCAPI calls
 * - Rollback: on error the optimistic quantity overrides are cleared
 *
 * NOTE: updateItemQuantity is currently unused because the QuantityPicker is disabled (KL-930).
 * Keep this logic ready for when quantity editing is unblocked.
 */
import {useCallback, useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import debounce from 'lodash/debounce';

import {useAccessToken, useCommerceApi, useShopperBasketsMutation} from '@salesforce/commerce-sdk-react';
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast';
import {
  API_ERROR_MESSAGE,
  TOAST_MESSAGE_REMOVED_ITEM_FROM_CART,
} from '@salesforce/retail-react-app/app/constants';
import {trackRemoveFromCartFromBasketLine} from '@amblique/king-living/app/analytics/ga4/remove-from-cart';
import {resolveBasketRemovalGroup} from '@amblique/king-living/app/utils/cart-utils';
import {enqueueBasketMutation} from '@amblique/king-living/app/utils/basket-mutation-queue';

const DEBOUNCE_WAIT = 750;

/**
 * Return only the child line items that actually belong to this parent.
 *
 * Defends against over-broad grouping (e.g. when multiple visible parents share
 * the same productId): only operate on children whose
 * `c_parentProductLineItemUUId` matches the parent's `itemId`. Children that
 * have no UUID linking (legacy or orphaned data) are kept so single-instance
 * complex products still cascade correctly.
 */
function filterOwnChildren(lineItem) {
  const children = lineItem?._childItems ?? [];

  return children.filter((child) => {
    const linkedUUID = child?.c_parentProductLineItemUUId;

    return !linkedUUID || linkedUUID === lineItem.itemId;
  });
}

/**
 * @returns {{
 *   updateItemQuantity: (lineItem: Object, newQuantity: number) => void,
 *   removeItem: (lineItem: Object) => Promise<void>,
 *   localQuantities: Object.<string, number>,
 *   removingItemIds: Set<string>,
 *   updatingItemIds: Set<string>,
 *   isUpdating: boolean,
 *   isRemoving: boolean,
 * }}
 */
export function useOptimisticCartMutations() {
  const intl = useIntl();
  const toast = useToast();
  const api = useCommerceApi();
  const {getTokenWhenReady} = useAccessToken();
  const {data: basket} = useCurrentBasket();
  const {data: customer} = useCurrentCustomer();
  const basketId = basket?.basketId;
  const currency = basket?.currency;

  // itemId → optimistic quantity override (cleared on server success / rollback on error)
  const [localQuantities, setLocalQuantities] = useState({});

  // Set of itemIds whose remove mutation is in-flight — overlays a spinner on the card
  // and hides intermediate basket re-renders caused by sequential child deletions.
  const [removingItemIds, setRemovingItemIds] = useState(new Set());

  // Set of itemIds whose quantity-update mutation is in-flight — shows a spinner overlay
  // while the debounced SCAPI call is pending and executing.
  const [updatingItemIds, setUpdatingItemIds] = useState(new Set());

  const [isUpdating, setIsUpdating] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const updateItemMutation = useShopperBasketsMutation('updateItemInBasket');
  const removeItemMutation = useShopperBasketsMutation('removeItemFromBasket');

  // Store mutations in refs so the debounced function always has the latest instance
  // without needing to be recreated every render when the mutation reference changes.
  const updateItemMutationRef = useRef(updateItemMutation);
  const removeItemMutationRef = useRef(removeItemMutation);
  const basketIdRef = useRef(basketId);

  useEffect(() => {
    updateItemMutationRef.current = updateItemMutation;
  });

  useEffect(() => {
    removeItemMutationRef.current = removeItemMutation;
  });

  useEffect(() => {
    basketIdRef.current = basketId;
  });

  const showError = useCallback(() => {
    toast({title: intl.formatMessage(API_ERROR_MESSAGE), status: 'error'});
  }, [intl, toast]);

  const showErrorRef = useRef(showError);

  useEffect(() => {
    showErrorRef.current = showError;
  });

  // Build debounced SCAPI call once on mount — reads current values via refs so
  // it never needs to be recreated, avoiding accidental cancellation on re-renders.
  const debouncedUpdateRef = useRef(null);

  useEffect(() => {
    debouncedUpdateRef.current = debounce(async (lineItem, newQuantity) => {
      const itemsToUpdate = [lineItem, ...filterOwnChildren(lineItem)];
      const allIds = itemsToUpdate.map((item) => item.itemId);
      const currentBasketId = basketIdRef.current;

      setIsUpdating(true);
      // Mark all related items as updating so the spinner overlay appears
      setUpdatingItemIds((prev) => {
        const next = new Set(prev);

        allIds.forEach((id) => next.add(id));

        return next;
      });

      try {
        // TODO(KINDW-43): Confirm SCAPI does NOT auto-cascade child quantities.
        // If it does, the child loop below can be removed.
        // KL-1029 — serialize the group against other basket mutations.
        await enqueueBasketMutation(async () => {
          for (const item of itemsToUpdate) {
            await updateItemMutationRef.current.mutateAsync({
              parameters: {basketId: currentBasketId, itemId: item.itemId},
              body: {productId: item.productId, quantity: newQuantity},
            });
          }
        });

        // Clear optimistic overrides once all SCAPI calls succeed
        setLocalQuantities((prev) => {
          const next = {...prev};

          allIds.forEach((id) => delete next[id]);

          return next;
        });
      } catch {
        // Rollback — remove optimistic overrides so the UI falls back to server values
        setLocalQuantities((prev) => {
          const next = {...prev};

          allIds.forEach((id) => delete next[id]);

          return next;
        });
        showErrorRef.current();
      } finally {
        setIsUpdating(false);
        setUpdatingItemIds((prev) => {
          const next = new Set(prev);

          allIds.forEach((id) => next.delete(id));

          return next;
        });
      }
    }, DEBOUNCE_WAIT);

    return () => {
      debouncedUpdateRef.current?.cancel();
    };
  }, []); // intentionally empty — reads current values via refs

  /**
   * Immediately update the displayed quantity (optimistic); debounce the SCAPI call.
   * Cascades to all _childItems of complex line items.
   */
  const updateItemQuantity = useCallback((lineItem, newQuantity) => {
    const itemsToUpdate = [lineItem, ...filterOwnChildren(lineItem)];

    setLocalQuantities((prev) => {
      const next = {...prev};

      itemsToUpdate.forEach((item) => {
        next[item.itemId] = newQuantity;
      });

      return next;
    });

    debouncedUpdateRef.current?.(lineItem, newQuantity);
  }, []);

  /** Cascade-remove configuration group (children first, then parent). */
  const removeItem = useCallback(async (lineItem) => {
    const {removalOrder} = resolveBasketRemovalGroup(lineItem, basket?.productItems);
    const children = removalOrder.slice(0, -1);
    const parent = removalOrder[removalOrder.length - 1] ?? lineItem;
    const allIds = removalOrder.map((item) => item.itemId);

    setRemovingItemIds((prev) => {
      const next = new Set(prev);

      allIds.forEach((id) => next.add(id));

      return next;
    });
    setIsRemoving(true);

    try {
      // KL-1029 — run the whole removal group (children first, then parent) as a
      // single serialized task so no concurrent basket mutation (delivery method,
      // promo code, deposit auto-sync) interleaves between deletions and writes a
      // stale pre-removal basket back into the cache, resurrecting the item.
      await enqueueBasketMutation(async () => {
        for (const child of children) {
          await removeItemMutationRef.current.mutateAsync({
            parameters: {basketId: basketIdRef.current, itemId: child.itemId},
          });
        }

        await removeItemMutationRef.current.mutateAsync(
          {parameters: {basketId: basketIdRef.current, itemId: parent.itemId}},
          {
            onSuccess: () => {
              void trackRemoveFromCartFromBasketLine({
                api,
                currency,
                customer,
                getToken: getTokenWhenReady,
                lineItem,
                productItems: basket?.productItems,
              });

              toast({
                title: intl.formatMessage(TOAST_MESSAGE_REMOVED_ITEM_FROM_CART, {quantity: 1}),
                status: 'success',
              });
            },
          }
        );
      });
    } catch {
      showError();
      // Clear removing state so the item becomes interactive again after failure
      setRemovingItemIds((prev) => {
        const next = new Set(prev);

        allIds.forEach((id) => next.delete(id));

        return next;
      });
      throw new Error('removeItem failed');
    } finally {
      setIsRemoving(false);
      // On success the item will be gone from the basket; clearing here is a safety net
      // in case a stale reference remains (e.g. optimistic list not yet reconciled).
      setRemovingItemIds((prev) => {
        const next = new Set(prev);

        allIds.forEach((id) => next.delete(id));

        return next;
      });
    }
  }, [api, basket?.productItems, currency, customer, getTokenWhenReady, intl, toast, showError]);

  return {
    isRemoving,
    isUpdating,
    localQuantities,
    removeItem,
    removingItemIds,
    updateItemQuantity,
    updatingItemIds,
  };
}
